<template>
  <div>
    <v-card
      id="programs"
      class="mb-4"
      flat
      tile
    >
      <v-card-title class="tab-title d-flex">
        <v-btn
          v-if="showProviderProfileNavBtn"
          @click="emit('toggleProviderProfileNav')"
          :aria-label="$bct('Toggle provider profile navigation')"
          data-cy="provider_profile_nav_icon"
          icon="menu_open"
          variant="text"
        />
        <h2 class="v-card-title py-0 px-0 d-flex align-center">
          {{ $bct(terms.programs) }}
        </h2>
        <v-spacer />
        <StandardButton
          v-if="!programLockEnabled"
          @click="openDraft()"
          :text="`Create new ${terms.program.toLowerCase()}`"
          data-testid="new-program-button"
          role="create"
        />
      </v-card-title>
      <v-divider />
      <v-card-text class="my-4">
        <v-row class="mb-2">
          <v-col class="fs-18">
            {{
              $bct(
                description ||
                  'Add classrooms, before or after school programs, summer programs etc.',
              )
            }}
          </v-col>
        </v-row>
        <v-row
          v-if="showBackToLocationsBtn"
          class="mt-2"
          style="min-height: 40px"
        >
          <!-- eslint-disable vue-router-rules/await-router-navigation -->
          <StandardButton
            @click="$router.push({ name: 'ProviderIndex' })"
            prepend-icon="arrow_back"
            role="link"
            text="Back to Locations"
          />
        </v-row>

        <v-row v-if="programs.length > 0">
          <v-col
            v-for="(program, index) in programs"
            :key="index"
            class="mb-4"
            cols="12"
            lg="4"
            md="6"
          >
            <v-card
              border
              flat
              tile
            >
              <v-card-title>
                <v-row>
                  <v-col cols="10">
                    <span
                      v-text="$bct(program.name)"
                      class="fs-22"
                      data-cy="program-name"
                    />
                  </v-col>
                  <v-col
                    class="d-flex justify-end pt-1"
                    cols="2"
                  >
                    <ActionMenu
                      @click:action:delete="destroy(program)"
                      @click:action:duplicate="duplicate(program)"
                      @click:action:edit="editProgram(program)"
                      :button-icon-size="20"
                      :items="actionItems"
                      :label="`Modify ${program.name}`"
                      button-data-cy="edit-program-button"
                      button-icon="more_vert"
                      left
                      x-small
                    />
                  </v-col>
                </v-row>
              </v-card-title>

              <v-card-text>
                <ProviderProgram
                  :dashboard="dashboard"
                  :program="program"
                />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <ProgramEditor
      @save="save"
      ref="programEditor"
      :age-groups="ageGroups"
      :processing="processing"
      :subsidy-programs="subsidyPrograms"
    />
  </div>

  <ConfirmDialog ref="confirmDialog" />
</template>

<script setup>
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import ActionMenu from '@/shared/components/ActionMenu.vue';
import ConfirmDialog from '@/shared/components/ConfirmDialog.vue';
import ProgramEditor from '@/shared/components/provider/ProgramEditor.vue';
import { ProgramParams } from '@/shared/services/program-params';
import ProviderProgram from '@/shared/components/provider/ProviderProgram.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import useTerms from '@/shared/composables/useTerms';

const $bct = inject('translator');

const props = defineProps({
  ageGroups: {
    type: Array,
    default: null,
  },
  description: {
    type: String,
    default: null,
  },
  providerId: {
    type: String,
    default: null,
  },
  regularHours: {
    type: Array,
    default: null,
  },
  showProviderProfileNavBtn: {
    type: Boolean,
    default: null,
  },
});

const emit = defineEmits(['progress', 'refresh', 'toggleProviderProfileNav']);

const { api: Api } = useApi();
const eventBus = useEventBus();
const route = useRoute();
const store = useStore();
const { terms } = useTerms();

const hours = ref([
  {
    day: 0,
    open: 700,
    close: 1800,
    included: true,
  },
  {
    day: 1,
    open: 700,
    close: 1800,
    included: true,
  },
  {
    day: 2,
    open: 700,
    close: 1800,
    included: true,
  },
  {
    day: 3,
    open: 700,
    close: 1800,
    included: true,
  },
  {
    day: 4,
    open: 700,
    close: 1800,
    included: true,
  },
  {
    day: 5,
    open: 700,
    close: 1800,
    included: false,
  },
  {
    day: 6,
    open: 700,
    close: 1800,
    included: false,
  },
]);
const confirmDialog = ref(null);
const dashboard = ref(null);
const processing = ref(false);
const programEditor = ref(null);
const programs = ref([]);
const subsidyPrograms = ref([]);

const programLockEnabled = computed(() => {
  return store.state.pages?.Provider?.features?.enable_program_lock === true;
});

const actionItems = computed(() => {
  let items = [{ avatar: 'edit', event: 'edit', title: 'Edit' }];

  if (!programLockEnabled.value) {
    items.push(
      { avatar: 'content_copy', event: 'duplicate', title: 'Duplicate' },
      { avatar: 'delete', event: 'delete', title: 'Delete' },
    );
  }
  return items;
});

const showBackToLocationsBtn = computed(() => {
  return !store.state.site?.enable_provider_v2 && route.query.backTo === 'ProviderIndex';
});

watch(() => props.providerId, load, { immediate: true });

async function destroy(program) {
  const confirm = await confirmDialog.value.confirmWithText(
    `Are you sure you want to remove this ${terms.value.program.toLowerCase()}?`,
  );
  if (!confirm) return;

  processing.value = true;
  Api.program.destroy(props.providerId, program.id, async () => {
    processing.value = false;
    await load();
    emit('refresh');
  });
}

async function duplicate(program) {
  const draft = JSON.parse(JSON.stringify(program));
  draft.id = null;
  if (!draft.hours) {
    draft.regular_hours = true;
    draft.hours = getDefaultHours();
  }

  await programEditor.value.open(draft);
}

async function editProgram(program) {
  const draft = JSON.parse(JSON.stringify(program));
  if (!draft.hours) {
    draft.regular_hours = true;
    draft.hours = getDefaultHours();
  }

  await programEditor.value.open(draft);
}

function getBlankProgram() {
  return {
    age_group_ids: [],
    age_max_months: true,
    age_min_months: true,
    custom: {},
    hours: getDefaultHours(),
    is_free: false,
    is_donation_based: false,
    regular_hours: true,
    season: null,
    schema_id: Object.values(store.state.schemas).filter(
      (schema) => schema.data_type === 'Program',
    )[0].id,
  };
}

function getDefaultHours() {
  if (props.regularHours) {
    return hours.value.map((newDay) => {
      const oldDay = props.regularHours.find((hours) => hours.day === newDay.day);
      const updatedNewDay = newDay;

      if (oldDay) {
        updatedNewDay.close = oldDay.close;
        updatedNewDay.included = true;
        updatedNewDay.open = oldDay.open;
        return updatedNewDay;
      }
      updatedNewDay.included = false;
      return updatedNewDay;
    }, this);
  }
  hours.value;
}

async function load() {
  processing.value = true;
  dashboard.value = null;
  programs.value = [];

  const { data } = await Api.program.index(props.providerId);
  programs.value = data.map((program) => new ProgramParams(program).asDraft());
  await loadSubsidyPrograms();
  processing.value = false;
  emit('progress', programs.value.length > 0 ? 100 : 0);
}

async function loadSubsidyPrograms() {
  const resp = await Api.shared.subsidyProgram.index();
  subsidyPrograms.value = resp.data;
}

async function openDraft() {
  const draft = getBlankProgram();
  await programEditor.value.open(draft);
}

function save(draft) {
  processing.value = true;
  const params = new ProgramParams(draft).asJson();
  Api.program.updateOrCreate(
    props.providerId,
    params,
    async () => {
      await load();
      processing.value = false;
      programEditor.value.close();
      eventBus.chime('Saved');
      emit('refresh');
    },
    (error) => {
      processing.value = false;
      const errors = error.response.data.errors;
      // Customize age min/max validation errors for clarity
      const allErrorMessages = errors.map((errorMessage) => {
        const ageMinMatch = errorMessage?.match(/Age min must be at least (\d+)/);
        if (ageMinMatch) {
          return `Unable to save your changes. The age set is below the minimum limit of ${ageMinMatch[1]} months.`;
        }

        const ageMaxMatch = errorMessage?.match(/Age max must be less than or equal to (\d+)/);
        if (ageMaxMatch) {
          return `Unable to save your changes. The age set exceeds the maximum limit of ${ageMaxMatch[1]} months.`;
        }
        return errorMessage;
      });

      allErrorMessages.forEach((message) => {
        eventBus.longChime(message);
      });
    },
  );
}
</script>

<style>
.h-56 {
  height: 56px;
}

.w-150 {
  width: 150px;
}
</style>
